/**
 * Utility functions for extracting and formatting error messages
 */

interface ApiErrorData {
  error?: {
    message?: string;
    details?: {
      operation?: string;
      reason?: string;
      [key: string]: unknown;
    };
  };
  message?: string;
}

interface DocumentProcessorError {
  status: string;
  error_type: string;
  message: string;
  details?: {
    invalid_properties?: string[];
    count?: number;
    suggestion?: string;
    file?: string;
    undefined_variable?: string;
    [key: string]: unknown;
  };
}

/**
 * Extract a user-friendly error message from an API error
 */
export function extractErrorMessage(error: unknown): {
  title: string;
  message: string;
  details?: string[];
  suggestion?: string;
} {
  // Default error response
  const defaultError = {
    title: 'Document Generation Failed',
    message: 'An unexpected error occurred while generating the document.',
  };

  if (!(error instanceof Error)) {
    return defaultError;
  }

  // Check if it's an ApiError with structured data
  const apiError = error as Error & { data?: unknown };
  if (apiError.data && typeof apiError.data === 'object') {
    const errorData = apiError.data as ApiErrorData;
    
    // Try to extract document processor error from the reason field
    if (errorData.error?.details?.reason) {
      const reason = errorData.error.details.reason;
      
      // Check if the reason contains a document processor error (JSON string)
      if (reason.includes('Document processor returned')) {
        try {
          // Extract the JSON part from the reason string
          const jsonMatch = reason.match(/\{.*\}$/);
          if (jsonMatch) {
            const processorError: DocumentProcessorError = JSON.parse(jsonMatch[0]);
            return formatDocumentProcessorError(processorError);
          }
        } catch (parseError) {
          console.warn('Failed to parse document processor error:', parseError);
        }
      }
      
      // If it's not a document processor error, use the reason as is
      return {
        title: 'Document Generation Failed',
        message: reason,
      };
    }
    
    // Use the main error message if available
    if (errorData.error?.message || errorData.message) {
      return {
        title: 'Document Generation Failed',
        message: errorData.error?.message || errorData.message || defaultError.message,
      };
    }
  }

  // Fallback to the error message
  return {
    title: 'Document Generation Failed',
    message: error.message || defaultError.message,
  };
}

/**
 * Format document processor specific errors
 */
function formatDocumentProcessorError(processorError: DocumentProcessorError): {
  title: string;
  message: string;
  details?: string[];
  suggestion?: string;
} {
  const { error_type, message, details } = processorError;

  switch (error_type) {
    case 'invalid_property_names':
      return {
        title: 'Invalid Property Names',
        message: 'The document template contains property names with invalid characters.',
        details: details?.invalid_properties?.slice(0, 5), // Show first 5 properties
        suggestion: details?.suggestion || 'Property names should only contain letters, numbers, and underscores.',
      };

    case 'undefined_variable':
      return {
        title: 'Missing Template Variable',
        message: 'The document template references a variable that is not available in the data.',
        details: details?.undefined_variable ? [details.undefined_variable] : undefined,
        suggestion: 'Please check that the template variables match the available data properties.',
      };

    case 'template_error':
      return {
        title: 'Template Processing Error',
        message: 'There was an error processing the document template.',
        details: details?.file ? [`File: ${details.file}`] : undefined,
        suggestion: 'Please check the template syntax and try again.',
      };

    case 'validation_error':
      return {
        title: 'Data Validation Error',
        message: 'The provided data does not match the expected format.',
        suggestion: 'Please ensure all required fields are properly filled.',
      };

    default:
      return {
        title: 'Document Processing Error',
        message: message || 'An error occurred while processing the document.',
        suggestion: 'Please try again or contact support if the problem persists.',
      };
  }
}

/**
 * Format error details for display
 */
export function formatErrorDetails(details?: string[]): string {
  if (!details || details.length === 0) {
    return '';
  }

  if (details.length === 1) {
    return details[0];
  }

  return details.map((detail, index) => `${index + 1}. ${detail}`).join('\n');
}
